import { httpClient } from '@/shared/api/httpClient';

import {
  importAnalysisSchema,
  importChangePageSchema,
  importConflictPageSchema,
  importIntakeResultSchema,
  importResolveResultSchema,
  importRollbackPreviewSchema,
  importRollbackResultSchema,
  importRunSummarySchema,
} from '../model/types';
import type {
  ImportAnalysis,
  ImportChangePage,
  ImportConflictKind,
  ImportConflictPage,
  ImportConflictState,
  ImportIntakeResult,
  ImportResolveResult,
  ImportRollbackPreview,
  ImportRollbackResult,
  ImportRunOptions,
  ImportRunSummary,
} from '../model/types';

/**
 * Czyste funkcje endpointów importu — zero Reacta, zero stanu.
 *
 * Każda odpowiedź przechodzi przez `zod`: rozjazd kontraktu ma wybuchać tam, gdzie powstał,
 * a nie trzy komponenty dalej (decyzja 74).
 */

/**
 * Faza 0 — wgranie pliku i utworzenie przebiegu.
 *
 * ⭐ Plik idzie **jako ciało żądania, nie jako tekst**: przy wsadach powyżej 100 MB
 * (decyzja 107) `await file.text()` wciągnąłby całość do pamięci przeglądarki i zniweczył
 * strumieniowanie, na którym stoi cała faza 0 po stronie serwera.
 *
 * ⚠ **`FormData`, a nie samo ciało** — i to nie jest kosmetyka. Surowe ciało nie niesie
 * NAZWY PLIKU, więc serwer zapisywał każdy przebieg jako „wsad bez nazwy": lista przebiegów
 * i raport traciły jedyną rzecz, po której człowiek rozpoznaje, co wtedy wgrał. Znalezione
 * na żywym ekranie. ⭐ Strumieniowanie zostaje: przeglądarka wysyła plik z `FormData`
 * kawałkami, a serwer czyta go `MultipartReader`-em nad gniazdem, bez buforowania.
 */
export async function startImport(file: File): Promise<ImportIntakeResult> {
  return importIntakeResultSchema.parse(
    await httpClient.upload<unknown>('/imports', asUpload(file)),
  ) as ImportIntakeResult;
}

/** Ponowne wgranie poprawionego pliku do TEGO SAMEGO przebiegu (decyzja 102). */
export async function reuploadImport(runId: number, file: File): Promise<ImportIntakeResult> {
  return importIntakeResultSchema.parse(
    await httpClient.upload<unknown>(`/imports/${runId}/file`, asUpload(file)),
  ) as ImportIntakeResult;
}

/**
 * ⚠ Nagłówka `Content-Type` NIE ustawiamy sami — przeglądarka musi dopisać do niego
 * `boundary`, którego nie znamy. Ręczny nagłówek rozłożyłby multiparta po stronie serwera.
 */
function asUpload(file: File): FormData {
  const form = new FormData();
  form.append('file', file, file.name);

  return form;
}

/** Zleca fazę 1. ⚠ Wraca natychmiast — analiza chodzi w tle. */
export async function requestAnalysis(runId: number): Promise<ImportRunSummary> {
  return importRunSummarySchema.parse(
    await httpClient.post<unknown>(`/imports/${runId}/analyze`, {}),
  ) as ImportRunSummary;
}

export async function fetchAnalysis(runId: number, signal?: AbortSignal): Promise<ImportAnalysis> {
  return importAnalysisSchema.parse(
    await httpClient.get<unknown>(`/imports/${runId}`, { ...(signal ? { signal } : {}) }),
  ) as ImportAnalysis;
}

export async function fetchRuns(signal?: AbortSignal): Promise<ImportRunSummary[]> {
  const response = await httpClient.get<unknown>('/imports', { ...(signal ? { signal } : {}) });

  return importRunSummarySchema.array().parse(response) as ImportRunSummary[];
}

export interface ConflictQuery {
  kind?: ImportConflictKind | undefined;
  state?: ImportConflictState | undefined;
  offset?: number | undefined;
  limit?: number | undefined;
}

export async function fetchConflicts(
  runId: number,
  query: ConflictQuery,
  signal?: AbortSignal,
): Promise<ImportConflictPage> {
  const params = new URLSearchParams();

  if (query.kind) params.set('kind', query.kind);
  if (query.state) params.set('state', query.state);
  params.set('offset', String(query.offset ?? 0));
  params.set('limit', String(query.limit ?? 50));

  return importConflictPageSchema.parse(
    await httpClient.get<unknown>(`/imports/${runId}/conflicts?${params.toString()}`, {
      ...(signal ? { signal } : {}),
    }),
  ) as ImportConflictPage;
}

export async function resolveConflict(
  runId: number,
  conflictId: number,
  kind: ImportConflictKind,
  choice: string,
): Promise<ImportResolveResult> {
  return importResolveResultSchema.parse(
    await httpClient.post<unknown>(`/imports/${runId}/conflicts/${conflictId}/resolve`, {
      kind,
      choice,
    }),
  ) as ImportResolveResult;
}

/** ⭐ Akcja hurtowa na CAŁEJ grupie — warunek wykonalności, nie wygoda. */
export async function resolveBulk(
  runId: number,
  kind: ImportConflictKind,
  choice: string,
): Promise<ImportResolveResult> {
  return importResolveResultSchema.parse(
    await httpClient.post<unknown>(`/imports/${runId}/conflicts/bulk`, { kind, choice }),
  ) as ImportResolveResult;
}

export async function mapType(
  runId: number,
  fileType: string,
  targetType: string,
): Promise<number> {
  return httpClient.post<number>(`/imports/${runId}/types`, { fileType, targetType });
}

export async function mapOwner(
  runId: number,
  fileOwner: string,
  targetKind: 'user' | 'team' | null,
  targetId: number | null,
): Promise<number> {
  return httpClient.post<number>(`/imports/${runId}/owners`, { fileOwner, targetKind, targetId });
}

/**
 * Opcje przebiegu (2026-09-04).
 *
 * ⚠ Serwer **przelicza konflikty** przy tej zmianie: „przypisuj także kontom nieaktywnym"
 * zdejmuje albo dokłada cały ich rodzaj, więc odpowiedź niesie nowe podsumowanie, a listę
 * pozycji trzeba pobrać na nowo.
 */
export async function setImportOptions(
  runId: number,
  options: ImportRunOptions,
): Promise<ImportRunSummary> {
  return importRunSummarySchema.parse(
    await httpClient.put<unknown>(`/imports/${runId}/options`, options),
  ) as ImportRunSummary;
}

/** Faza 3 — zapis. ⛔ Wchodzi cały albo wcale. */
export async function commitImport(runId: number): Promise<ImportRunSummary> {
  return importRunSummarySchema.parse(
    await httpClient.post<unknown>(`/imports/${runId}/commit`, {}),
  ) as ImportRunSummary;
}

export async function abandonImport(runId: number): Promise<ImportRunSummary> {
  return importRunSummarySchema.parse(
    await httpClient.post<unknown>(`/imports/${runId}/abandon`, {}),
  ) as ImportRunSummary;
}

export interface ChangeQuery {
  kind?: string | undefined;
  query?: string | undefined;
  offset?: number | undefined;
  limit?: number | undefined;
}

/** ⛔ Zawsze filtrowane i stronicowane — raport z 71 tys. pozycji jest zapytaniem, nie listą. */
export async function fetchChanges(
  runId: number,
  query: ChangeQuery,
  signal?: AbortSignal,
): Promise<ImportChangePage> {
  const params = new URLSearchParams();

  if (query.kind) params.set('kind', query.kind);
  if (query.query) params.set('query', query.query);
  params.set('offset', String(query.offset ?? 0));
  params.set('limit', String(query.limit ?? 100));

  return importChangePageSchema.parse(
    await httpClient.get<unknown>(`/imports/${runId}/changes?${params.toString()}`, {
      ...(signal ? { signal } : {}),
    }),
  ) as ImportChangePage;
}

/** Rachunek wycofania — ⛔ bez zapisu. */
export async function fetchRollbackPreview(
  runId: number,
  signal?: AbortSignal,
): Promise<ImportRollbackPreview> {
  return importRollbackPreviewSchema.parse(
    await httpClient.get<unknown>(`/imports/${runId}/rollback`, { ...(signal ? { signal } : {}) }),
  ) as ImportRollbackPreview;
}

/** ⭐ Zrywa PO POWODZIE, nie po pozycji. */
export async function rollbackImport(
  runId: number,
  forceReasons: string[],
): Promise<ImportRollbackResult> {
  return importRollbackResultSchema.parse(
    await httpClient.post<unknown>(`/imports/${runId}/rollback`, { forceReasons }),
  ) as ImportRollbackResult;
}
